<template>
  <div class="c-email-template__block" :style="rootStyles">
    <table
      align="center"
      border="0"
      cellpadding="0"
      cellspacing="0"
      role="presentation"
      class="c-email-template__columns"
      :style="tableStyles"
    >
      <tr>
        <td :style="contentStyles">
          <table
            border="0"
            cellpadding="0"
            cellspacing="0"
            class="c-email-template__columns"
          >
            <tr v-if="blockModel.type === 'oneColumn'">
              <ak-email-template-column
                :block-index="blockIndex"
                :children="blockModel.content"
                :column-spacing="columnSpacing"
                :column-index="0"
                :block-settings="blockModel.settings"
                :settings="settings"
                :opts="opts"
                :is-builder="isBuilder"
                :width-decimal="columnConfig[blockModel.type][1]"
                :width="columnConfig[blockModel.type][0]"
                :update-column="updateContent"
                :drawer-container="drawerContainer"
              />
            </tr>
            <tr v-else>
              <template
                v-for="(content, contentIndex) in blockModel.content"
                :key="contentIndex"
              >
                <ak-email-template-column
                  :block-index="blockIndex"
                  :children="content"
                  :column-spacing="columnSpacing"
                  :column-index="contentIndex"
                  :block-settings="blockModel.settings"
                  :settings="settings"
                  :opts="opts"
                  :is-builder="isBuilder"
                  :width-decimal="
                    columnConfig[blockModel.type][contentIndex][1]
                  "
                  :width="columnConfig[blockModel.type][contentIndex][0]"
                  :update-column="updateContent"
                  :drawer-container="drawerContainer"
                />
              </template>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import config from '../../utils/config'
import Column from '../column/Column.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-block></ak-email-template-block>
   */
  name: 'AkEmailTemplateBlock',

  /**
   * @type {Boolean}
   * @description Causes the vue application to preserve HTML comments
   */
  comments: true,

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Column.name]: Column,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The block that we want to render/edit
     */
    modelValue: {
      type: Object,
      default: () => ({
        type: 'oneColumn',
        settings: { ...config.defaultBlockOptions },
        content: [],
      }),
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    opts: {
      type: Object,
      default: () => ({}),
    },

    /**
     * @type {Boolean}
     * @description Determines if we are to render the block for the
     * builder component (drag and drop editor)
     */
    isBuilder: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const columnConfig = config.columns

    const blockModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    /**
     * @returns {Number}
     * @description Returns the column spacing setting for the template
     */
    const columnSpacing = computed(() => {
      /* istanbul ignore next */
      return blockModel.value.settings.flushSpacing === true ? 0 : 10
    })

    /**
     * @returns {Object}
     * @description returns the root styles for the component
     */
    const rootStyles = computed(() => {
      return {
        background: blockModel.value.settings.background,
        backgroundColor: blockModel.value.settings.background,
        margin: '0px auto',
        maxWidth: '600px',
        verticalAlign: blockModel.value.settings.columnAlignment,
      }
    })

    /**
     * @returns {Object}
     * @description returns the table styles for the component
     */
    const tableStyles = computed(() => {
      return {
        background: blockModel.value.settings.background,
        backgroundColor: blockModel.value.settings.background,
        width: '100%',
      }
    })

    /**
     * @returns {Object}
     * @description returns the content styles for the component
     */
    const contentStyles = computed(() => {
      return {
        direction: 'ltr',
        textAlign: 'center',
        paddingTop: blockModel.value.settings.flushSpacing
          ? '0'
          : `${blockModel.value.settings.spacing.top}px`,
        paddingRight: blockModel.value.settings.flushSpacing
          ? '0'
          : `${blockModel.value.settings.spacing.right}px`,
        paddingBottom: blockModel.value.settings.flushSpacing
          ? '0'
          : `${blockModel.value.settings.spacing.bottom}px`,
        paddingLeft: blockModel.value.settings.flushSpacing
          ? '0'
          : `${blockModel.value.settings.spacing.left}px`,
        msoPaddingAlt: `0`,
      }
    })

    /**
     * @param {Object} content
     * @param {Undefined|Number} columnIndex
     * @description Updates the content blocks within the parent block. this
     * is triggered from the drag and drop in the child components, this is due
     * to Vue not having full reactivity in the nested arrays, so we use this
     * method to keep the template data in sync when child content is moved
     * around in side the column components.
     */
    const updateContent = (content, columnIndex) => {
      if (blockModel.value.type !== 'oneColumn') {
        blockModel.value.content[columnIndex] = content
      } else {
        blockModel.value.content = content
      }
    }

    return {
      columnConfig,
      columnSpacing,
      rootStyles,
      tableStyles,
      contentStyles,
      updateContent,
      blockModel,
    }
  },
})
</script>
